import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { CustomSelect } from 'apps/sigma2-ui/src/components/ui/CustomSelect';
import { CustomSelectOption } from 'apps/sigma2-ui/src/components/ui/CustomSelect';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { TransformUniqueValuesByColumnItem } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { CardContent } from '@mui/material';
import { Chip } from '@mui/material';
import { Alert } from '@mui/material';
import { Typography } from '@mui/material';
import { Paper } from '@mui/material';
import { Stack } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { LinearProgress } from '@mui/material';
import { TextField } from '@mui/material';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { Divider } from 'apps/sigma2-ui/src/components/ui/Divider';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { RunCopilotModelExperimentationRequestBodySchema } from 'libs/end-to-end/src/types/databases/revisions/copilot-model-experimentation-input';
import { HiringCopilotExperimentationRequestSchema } from 'libs/end-to-end/src/types/databases/revisions/copilot-model-experimentation-input';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { SuccessPhenotypeFilterInput } from 'libs/end-to-end/src/success-phenotype';
import { z } from 'zod';
import { ModelExperimentationMultiColumnFilterSelections } from 'apps/sigma2-ui/src/components/ModelExperimentationMultiColumnFilterSelections';
import { ModelExperimentationFilterDefinitionForSelections } from 'apps/sigma2-ui/src/components/ModelExperimentationMultiColumnFilterSelections';
import { FocusAreaDefinition } from 'libs/end-to-end/src/types/databases/focus-areas/focus-area-definition';
import { FilterSetDefinitions } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { resolveFilterSet } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineFilterSetName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FocusAreaWithFilterSetNames } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getHiringCopilotRequestDropdowns } from 'libs/end-to-end/src/utils/get-hiring-copilot-dropdowns';

/** Format Zod validation errors so the UI can show which field(s) failed. Exported for tests. */
export function formatZodValidationError(error: z.ZodError): string {
  const lines = error.issues.map((issue) => {
    const path = issue.path.length > 0 ? issue.path.join('.') : 'payload';
    return `• ${path}: ${issue.message}`;
  });
  return lines.join('\n');
}

type ModelType = 'hiring-copilot' | 'key-factors' | 'promotion-copilot';

type ExecutionStatus =
  | 'idle'
  | 'starting'
  | 'polling'
  | 'succeeded'
  | 'failed'
  | 'timeout';

interface ModelExperimentationTabProps {
  uniqueValuesByColumn: TransformUniqueValuesByColumnItem[];
  revisionId: string;
  organizationId: string;
  databaseId: string;
  /** Metric column options from revision schema (fallback when no Performance FA). */
  possibleMetricSelections: string[];
  /** All filter sets from revision; Key Factors filter options use the Performance FA filter set (dataIntelligenceDeepDive). */
  filterSets: FilterSetDefinitions;
  /**
   * Fallback filter column options when Performance FA filter set cannot be resolved.
   * Key Factors filter options come from the Performance focus area's filter set when available.
   */
  possibleFilterColumnSelections?: string[];
  /** Performance focus area and other FAs from revision; Key Factors uses only Performance FA metrics. */
  focusAreaDefinitions?: FocusAreaDefinition[];
}

const MODEL_OPTIONS: CustomSelectOption[] = [
  { value: 'hiring-copilot', label: 'Hiring Copilot' },
  { value: 'key-factors', label: 'Key Factors' },
  { value: 'promotion-copilot', label: 'Promotion Copilot' },
];

/** Hiring copilot filter columns (aligned with HiringCopilotView dropdowns). */
const HIRING_COPILOT_FILTER_COLUMNS = [
  'department',
  'job_title',
  'location',
] as const;

/** Promotion copilot filter columns (same as hiring: department, job_title, location). */
const PROMOTION_COPILOT_FILTER_COLUMNS = [
  'department',
  'job_title',
  'location',
] as const;

// API Response Schemas
const RunModelResponse = z.object({
  executionId: z.string(),
});

const PollModelResponse = z.object({
  result: z.enum(['success', 'failed', 'running']),
});

const ExecutionStatusAlert: React.FC<{
  status: ExecutionStatus;
  errorMessage?: string;
  elapsedTime?: number;
  revisionId?: string;
}> = ({ status, errorMessage, elapsedTime, revisionId }) => {
  if (status === 'idle' || status === 'starting') {
    return null;
  }

  const getAlertProps = () => {
    switch (status) {
      case 'polling':
        return {
          severity: 'info' as const,
          icon: <CircularProgress size={20} />,
          title: 'Running Model',
          message: `Execution in progress... ${elapsedTime ? `(${Math.floor(elapsedTime / 1000)}s elapsed)` : ''}`,
        };
      case 'succeeded':
        return {
          severity: 'success' as const,
          icon: <CheckCircleOutlineIcon />,
          title: 'Run Complete',
          message: revisionId
            ? `View models in MLflow under experiment revision_${revisionId}_experimental`
            : 'The model has completed successfully. Results are ready for review.',
        };
      case 'failed':
        return {
          severity: 'error' as const,
          icon: <ErrorOutlineIcon />,
          title: 'Model Execution Failed',
          message: errorMessage || 'The model execution encountered an error.',
        };
      case 'timeout':
        return {
          severity: 'warning' as const,
          icon: <ErrorOutlineIcon />,
          title: 'Execution Timeout',
          message:
            'The model execution timed out after 5 minutes. Please try again or contact support if the issue persists.',
        };
      default:
        return null;
    }
  };

  const alertProps = getAlertProps();
  if (!alertProps) return null;

  return (
    <Alert severity={alertProps.severity} icon={alertProps.icon} sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {alertProps.title}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ whiteSpace: 'pre-wrap' }}
      >
        {alertProps.message}
      </Typography>
    </Alert>
  );
};

export const ModelExperimentationTab: React.FC<
  ModelExperimentationTabProps
> = ({
  uniqueValuesByColumn,
  revisionId,
  organizationId,
  databaseId,
  possibleMetricSelections,
  filterSets,
  possibleFilterColumnSelections,
  focusAreaDefinitions,
}): React.ReactElement | null => {
  const language = useLanguage();
  const { userAuthenticatedApiCall } = useUser();

  const { keyFactorsMetricSelections, keyFactorsFilterColumnSelections } =
    useMemo(() => {
      const performanceFa = focusAreaDefinitions?.find(
        (fa) => fa.focusArea === 'Performance',
      );
      const focusAreasForFilterSetName: FocusAreaWithFilterSetNames[] = (
        focusAreaDefinitions ?? []
      ).map((fa) => ({
        focusArea: fa.focusArea,
        dataIntelligenceDeepDiveFilterSetName:
          fa.toolsDefinition?.dataIntelligenceDeepDiveDefinition
            ?.filterSetName ?? null,
        binScatterMultiSelectFilterSetName:
          fa.toolsDefinition?.binScatterMultiSelectDefinition?.filterSetName ??
          null,
      }));
      const filterSetName = determineFilterSetName({
        feature: 'successFactors',
        focusArea: 'Performance',
        focusAreas: focusAreasForFilterSetName,
        filterSetDefinitionsWithValues:
          filterSets as unknown as FilterSetDefinitionWithValues[],
        analysisName: undefined,
        pipelineAnalysisConfiguration: undefined,
      });
      const performanceFilterSet = resolveFilterSet({
        filterSets,
        filterSetName,
      });
      return {
        keyFactorsMetricSelections:
          performanceFa?.metrics.map((m) => m.columnHeaderValue) ??
          possibleMetricSelections,
        keyFactorsFilterColumnSelections:
          performanceFilterSet?.filterDefinitions.map(
            (f) => f.columnHeaderValue,
          ) ??
          possibleFilterColumnSelections ??
          [],
      };
    }, [
      focusAreaDefinitions,
      possibleMetricSelections,
      possibleFilterColumnSelections,
      filterSets,
    ]);

  const keyFactorsFilterDefinitions: ModelExperimentationFilterDefinitionForSelections[] =
    useMemo(() => {
      return keyFactorsFilterColumnSelections.map((columnName) => ({
        columnHeaderValue: columnName,
        uniqueValues:
          uniqueValuesByColumn.find((item) => item.columnName === columnName)
            ?.uniqueValues ?? [],
      }));
    }, [keyFactorsFilterColumnSelections, uniqueValuesByColumn]);

  const [selectedModel, setSelectedModel] =
    useState<ModelType>('hiring-copilot');
  const [previousDaysOfApplicants, setPreviousDaysOfApplicants] = useState<
    number | undefined
  >(undefined);
  const [selectedKeyFactorsMetric, setSelectedKeyFactorsMetric] = useState<
    string | undefined
  >(undefined);
  const [executionStatus, setExecutionStatus] =
    useState<ExecutionStatus>('idle');
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [keyFactorsFilterSelections, setKeyFactorsFilterSelections] = useState<
    Record<string, string[]>
  >({});
  const [hiringCopilotFilterSelections, setHiringCopilotFilterSelections] =
    useState<Record<string, string[]>>({});
  const [
    promotionCopilotFilterSelections,
    setPromotionCopilotFilterSelections,
  ] = useState<Record<string, string[]>>({});

  const hiringCopilotFilterDefinitions: ModelExperimentationFilterDefinitionForSelections[] =
    useMemo(() => {
      return [...HIRING_COPILOT_FILTER_COLUMNS].map((columnName) => ({
        columnHeaderValue: columnName,
        uniqueValues:
          uniqueValuesByColumn.find((item) => item.columnName === columnName)
            ?.uniqueValues ?? [],
      }));
    }, [uniqueValuesByColumn]);

  const promotionCopilotFilterDefinitions: ModelExperimentationFilterDefinitionForSelections[] =
    useMemo(() => {
      return [...PROMOTION_COPILOT_FILTER_COLUMNS].map((columnName) => ({
        columnHeaderValue: columnName,
        uniqueValues:
          uniqueValuesByColumn.find((item) => item.columnName === columnName)
            ?.uniqueValues ?? [],
      }));
    }, [uniqueValuesByColumn]);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const elapsedTimeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (elapsedTimeIntervalRef.current) {
      clearInterval(elapsedTimeIntervalRef.current);
      elapsedTimeIntervalRef.current = null;
    }
    pollingStartTimeRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const pollModelExecution = useCallback(
    async (execId: string) => {
      try {
        const response = await userAuthenticatedApiCall({
          responseSchema: PollModelResponse,
          url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/revisions/${revisionId}/copilot-model-experimentation/${execId}`,
          requestOptions: {
            method: 'GET',
          },
        });

        if (response && response.resultType === 'ok') {
          const result = response.value.result;

          switch (result) {
            case 'success':
              stopPolling();
              setExecutionStatus('succeeded');
              break;
            case 'failed':
              stopPolling();
              setExecutionStatus('failed');
              setErrorMessage('Model execution failed');
              break;
            case 'running':
              // Continue polling
              break;
          }
        } else {
          stopPolling();
          setExecutionStatus('failed');
          setErrorMessage('Failed to check execution status');
        }
      } catch {
        stopPolling();
        setExecutionStatus('failed');
        setErrorMessage('Error polling execution status');
      }
    },
    [
      userAuthenticatedApiCall,
      stopPolling,
      organizationId,
      databaseId,
      revisionId,
      selectedModel,
    ],
  );

  const handleRunModel = useCallback(async () => {
    setExecutionStatus('starting');
    setErrorMessage(undefined);
    setElapsedTime(0);

    const buildRequestBody =
      (): RunCopilotModelExperimentationRequestBodySchema => {
        switch (selectedModel) {
          case 'promotion-copilot': {
            const valuesForColumn = (columnName: string): string[] => {
              const selected = promotionCopilotFilterSelections[columnName];
              if (selected && selected.length > 0) return selected;
              return (
                uniqueValuesByColumn.find(
                  (item) => item.columnName === columnName,
                )?.uniqueValues ?? []
              );
            };
            return {
              modelType: 'promotion-copilot',
              dropdowns: {
                department: valuesForColumn('department'),
                job_title: valuesForColumn('job_title'),
                location: valuesForColumn('location'),
              },
            };
          }
          case 'key-factors': {
            const filters: FilterDefinitionForExtras[] = Object.entries(
              keyFactorsFilterSelections,
            )
              .filter(([, values]) => values.length > 0)
              .map(([columnHeaderValue, values]) => ({
                columnHeaderValue,
                values,
              }));
            const extras = SuccessPhenotypeFilterInput.safeParse({
              metric: selectedKeyFactorsMetric ?? '',
              filters,
            });
            if (!extras.success) {
              throw new Error(
                `Validation failed:\n${formatZodValidationError(extras.error)}`,
              );
            }
            return {
              modelType: 'key-factors',
              metric: extras.data.metric,
              filters: extras.data.filters,
            };
          }
          case 'hiring-copilot':
          default: {
            const hiringPayload = {
              modelType: 'hiring-copilot' as const,
              dropdowns: getHiringCopilotRequestDropdowns({
                uniqueValuesByColumn,
                numPreviousDaysOfApplicants: previousDaysOfApplicants ?? 14,
                filterSelections: hiringCopilotFilterSelections,
              }),
            };
            const parsed =
              HiringCopilotExperimentationRequestSchema.safeParse(
                hiringPayload,
              );
            if (!parsed.success) {
              throw new Error(
                `Validation failed:\n${formatZodValidationError(parsed.error)}`,
              );
            }
            return parsed.data;
          }
        }
      };

    try {
      const response = await userAuthenticatedApiCall({
        responseSchema: RunModelResponse,
        url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/revisions/${revisionId}/copilot-model-experimentation`,
        requestOptions: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(buildRequestBody()),
        },
      });

      if (response && response.resultType === 'ok') {
        const execId = response.value.executionId;
        setExecutionId(execId);
        setExecutionStatus('polling');

        pollingStartTimeRef.current = Date.now();
        elapsedTimeIntervalRef.current = setInterval(() => {
          if (pollingStartTimeRef.current) {
            setElapsedTime(Date.now() - pollingStartTimeRef.current);
          }
        }, 1000);

        pollingIntervalRef.current = setInterval(() => {
          if (pollingStartTimeRef.current) {
            const elapsed = Date.now() - pollingStartTimeRef.current;
            // 5 minutes = 300,000 milliseconds
            if (elapsed >= 300000) {
              stopPolling();
              setExecutionStatus('timeout');
              return;
            }
          }
          void pollModelExecution(execId);
        }, 5000);

        void pollModelExecution(execId);
      } else {
        setExecutionStatus('failed');
        setErrorMessage('Failed to start model execution');
      }
    } catch (e) {
      setExecutionStatus('failed');
      setErrorMessage(
        e instanceof Error ? e.message : 'Error starting model execution',
      );
    }
  }, [
    userAuthenticatedApiCall,
    pollModelExecution,
    stopPolling,
    organizationId,
    databaseId,
    revisionId,
    previousDaysOfApplicants,
    selectedKeyFactorsMetric,
    selectedModel,
    keyFactorsFilterSelections,
    hiringCopilotFilterSelections,
    promotionCopilotFilterSelections,
    uniqueValuesByColumn,
  ]);

  const isRunning =
    executionStatus === 'starting' || executionStatus === 'polling';

  //eslint-disable-next-line complexity
  const renderModelContent = () => {
    switch (selectedModel) {
      case 'key-factors':
        return (
          <Stack spacing={3} sx={{ mt: 3 }}>
            <ExecutionStatusAlert
              status={executionStatus}
              errorMessage={errorMessage}
              elapsedTime={elapsedTime}
              revisionId={revisionId}
            />
            {isRunning && (
              <Box>
                <LinearProgress />
              </Box>
            )}

            <Card>
              <CardContent>
                <Box>
                  <SettingsIcon
                    sx={{ mr: 1.5, color: 'primary.main', fontSize: 20 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Configuration
                  </Typography>
                </Box>

                <Divider direction="horizontal" />

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}
                  >
                    Metric
                  </Typography>
                  <CustomSelect
                    selectedValue={selectedKeyFactorsMetric ?? ''}
                    options={keyFactorsMetricSelections.map((m) => ({
                      value: m,
                      label: m,
                    }))}
                    onChange={(value) =>
                      setSelectedKeyFactorsMetric(value || undefined)
                    }
                    placeholder="Select a metric..."
                  />
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box>
                  <InfoOutlinedIcon
                    sx={{ mr: 1.5, color: 'info.main', fontSize: 20 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Model Inputs
                  </Typography>
                </Box>

                <Divider direction="horizontal" />

                <ModelExperimentationMultiColumnFilterSelections
                  filterDefinitions={keyFactorsFilterDefinitions}
                  selections={keyFactorsFilterSelections}
                  onSelectionsChange={setKeyFactorsFilterSelections}
                  disabled={isRunning}
                  accordionTitle="Filters"
                  emptyMessage="No filter columns in schema"
                />
              </CardContent>
            </Card>

            <Paper>
              <Button
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    void handleRunModel();
                  },
                }}
                disabled={isRunning || !selectedKeyFactorsMetric}
                text={
                  isRunning
                    ? 'Running Model...'
                    : executionStatus === 'succeeded'
                      ? 'Run Again'
                      : 'Run Key Factors Model'
                }
              />
              {executionId && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  Execution ID: {executionId}
                </Typography>
              )}
            </Paper>
          </Stack>
        );

      case 'promotion-copilot':
        return (
          <Stack spacing={3} sx={{ mt: 3 }}>
            <ExecutionStatusAlert
              status={executionStatus}
              errorMessage={errorMessage}
              elapsedTime={elapsedTime}
              revisionId={revisionId}
            />

            {isRunning && (
              <Box>
                <LinearProgress />
              </Box>
            )}

            <Card>
              <CardContent>
                <Box>
                  <InfoOutlinedIcon
                    sx={{ mr: 1.5, color: 'info.main', fontSize: 20 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Model Inputs
                  </Typography>
                </Box>

                <Divider direction={'horizontal'} />

                <ModelExperimentationMultiColumnFilterSelections
                  filterDefinitions={promotionCopilotFilterDefinitions}
                  selections={promotionCopilotFilterSelections}
                  onSelectionsChange={setPromotionCopilotFilterSelections}
                  disabled={isRunning}
                  accordionTitle="Filters"
                  emptyMessage="No filter columns available"
                />
              </CardContent>
            </Card>

            <Paper>
              <Button
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    void handleRunModel();
                  },
                }}
                disabled={isRunning}
                text={
                  isRunning
                    ? 'Running Model...'
                    : executionStatus === 'succeeded'
                      ? 'Run Again'
                      : 'Run Promotion Copilot Model'
                }
              />
              {executionId && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  Execution ID: {executionId}
                </Typography>
              )}
            </Paper>
          </Stack>
        );

      case 'hiring-copilot':
        return (
          <Stack spacing={3} sx={{ mt: 3 }}>
            <ExecutionStatusAlert
              status={executionStatus}
              errorMessage={errorMessage}
              elapsedTime={elapsedTime}
              revisionId={revisionId}
            />

            {isRunning && (
              <Box>
                <LinearProgress />
              </Box>
            )}

            <Card>
              <CardContent>
                <Box>
                  <SettingsIcon
                    sx={{ mr: 1.5, color: 'primary.main', fontSize: 20 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Configuration
                  </Typography>
                </Box>

                <Divider direction="horizontal" />

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}
                  >
                    Previous Days of Applicants
                  </Typography>
                  <TextField
                    type="number"
                    value={previousDaysOfApplicants ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setPreviousDaysOfApplicants(undefined);
                      } else {
                        const num = parseInt(value, 10);
                        if (!isNaN(num) && num >= 1) {
                          setPreviousDaysOfApplicants(num);
                        }
                      }
                    }}
                    disabled={isRunning}
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Enter number of days (optional)"
                    inputProps={{ min: 1 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center">
                    <InfoOutlinedIcon
                      sx={{ mr: 1.5, color: 'info.main', fontSize: 20 }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Model Inputs
                    </Typography>
                  </Stack>
                  <Button
                    behavior={{
                      type: 'onClick',
                      onClick: () => setHiringCopilotFilterSelections({}),
                    }}
                    disabled={isRunning}
                    text="Use schedule-compatible defaults"
                    size="small"
                    variant="primary"
                  />
                </Stack>

                <Divider direction={'horizontal'} />

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    To train a model compatible with the automated schedule, use
                    schedule-compatible defaults (all departments, locations,
                    and job titles). The schedule always runs with all available
                    values for these columns.
                  </Typography>
                </Alert>

                <ModelExperimentationMultiColumnFilterSelections
                  filterDefinitions={hiringCopilotFilterDefinitions}
                  selections={hiringCopilotFilterSelections}
                  onSelectionsChange={setHiringCopilotFilterSelections}
                  disabled={isRunning}
                  accordionTitle="Filters"
                  emptyMessage="No filter columns available"
                />
              </CardContent>
            </Card>

            <Paper>
              <Button
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    void handleRunModel();
                  },
                }}
                disabled={isRunning}
                text={
                  isRunning
                    ? 'Running Model...'
                    : executionStatus === 'succeeded'
                      ? 'Run Again'
                      : `Run ${selectedModel === 'hiring-copilot' ? 'Hiring' : 'Promotion'} Copilot Model`
                }
              />
              {executionId && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  Execution ID: {executionId}
                </Typography>
              )}
            </Paper>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Box>
          <S2Typography
            variant="headline-5"
            translatedContent={language.dangerousUserInput({
              value: 'Model Experimentation',
            })}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, mb: 3 }}
          >
            Test and validate copilot models with your current database
            configuration
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Select Model
            </Typography>
            <CustomSelect
              selectedValue={selectedModel}
              options={MODEL_OPTIONS}
              onChange={(value) => setSelectedModel(value as ModelType)}
              placeholder="Select a model..."
            />
          </CardContent>
        </Card>

        {renderModelContent()}
      </Stack>
    </Box>
  );
};
